import React from 'react';
import { Link } from 'react-router-dom';
import {
  UserCheck,
  FileSpreadsheet,
  Share2,
  ShieldCheck,
  Cookie,
  User,
  Globe,
  FileText,
  Mail,
  Phone,
  Shield,
  Lock
} from 'lucide-react';

const PrivacyPolicy = () => {
  const sections = [
    {
      num: '1',
      title: 'Information We Collect',
      desc: 'We collect personal information that you provide to us directly, such as your name, email address, phone number, shipping address, payment information, and order details.',
      icon: UserCheck
    },
    {
      num: '2',
      title: 'How We Use Your Information',
      desc: 'We use your information to process and fulfill orders, communicate with you, provide customer support, improve our website and services, send marketing communications (if you opt in), and comply with legal obligations.',
      icon: FileSpreadsheet
    },
    {
      num: '3',
      title: 'Information Sharing',
      desc: 'We do not sell your personal information. We may share your information with trusted service providers who assist us in operating our website, processing payments, delivering orders, or conducting business activities, subject to confidentiality obligations.',
      icon: Share2
    },
    {
      num: '4',
      title: 'Data Security',
      desc: 'We implement appropriate technical and organizational measures to protect your personal information from unauthorized access, alteration, disclosure or destruction.',
      icon: ShieldCheck
    },
    {
      num: '5',
      title: 'Cookies & Tracking',
      desc: 'We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. You can manage cookie preferences in your browser settings.',
      icon: Cookie
    },
    {
      num: '6',
      title: 'Your Rights',
      desc: 'You have the right to access, update, correct or delete your personal information. You may also opt out of marketing communications at any time. To exercise your rights, contact us using the details below.',
      icon: User
    },
    {
      num: '7',
      title: 'International Transfers',
      desc: 'Your information may be transferred to and processed in countries outside your own. We ensure appropriate safeguards are in place to protect your information in accordance with this policy.',
      icon: Globe
    },
    {
      num: '8',
      title: 'Changes to This Policy',
      desc: 'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date.',
      icon: FileText
    }
  ];

  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '40px 24px 80px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
        
        {/* Breadcrumbs */}
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, marginBottom: '24px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: '#0f172a', fontWeight: 700 }}>Privacy Policy</span>
        </div>

        {/* TOP HERO HEADER WITH SHIELD GRAPHIC */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'center', marginBottom: '48px' }}>
          <div>
            <h1 style={{ fontSize: '44px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, marginBottom: '16px', letterSpacing: '-0.02em' }}>
              Privacy Policy
            </h1>
            <div style={{ width: '40px', height: '3px', background: '#dc2626', borderRadius: '2px', marginBottom: '20px' }} />
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#64748b', marginBottom: '16px' }}>
              Last updated: May 14, 2025
            </div>
            <p style={{ fontSize: '14.5px', color: '#475569', lineHeight: 1.7, maxWidth: '520px', margin: 0 }}>
              InfraEdge (“we”, “us” or “our”) respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose and safeguard your information when you visit our website or make a purchase.
            </p>
          </div>

          {/* Right Vector Security Shield Graphic */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <div style={{ position: 'relative', width: '280px', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* Outer Orbit Dots Circle */}
              <div style={{ position: 'absolute', width: '260px', height: '260px', border: '2px dashed #fecaca', borderRadius: '50%', pointerEvents: 'none' }} />
              
              {/* Center Red Shield Card */}
              <div style={{ width: '120px', height: '140px', background: '#ffffff', border: '2px solid #dc2626', borderRadius: '24px 24px 60px 60px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 30px rgba(220,38,38,0.15)', zIndex: 2 }}>
                <Lock size={48} color="#dc2626" />
              </div>

              {/* Orbiting Red Badges */}
              <div style={{ position: 'absolute', top: '10px', left: '20px', width: '36px', height: '36px', borderRadius: '50%', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                <UserCheck size={18} />
              </div>

              <div style={{ position: 'absolute', top: '10px', right: '20px', width: '36px', height: '36px', borderRadius: '50%', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                <Mail size={18} />
              </div>

              <div style={{ position: 'absolute', bottom: '20px', left: '10px', width: '36px', height: '36px', borderRadius: '50%', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                <ShieldCheck size={18} />
              </div>

              <div style={{ position: 'absolute', bottom: '20px', right: '10px', width: '36px', height: '36px', borderRadius: '50%', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                <FileText size={18} />
              </div>
            </div>
          </div>
        </div>

        {/* 8 POLICY SECTIONS GRID */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px', marginBottom: '48px' }}>
          {sections.map((sec) => {
            const Icon = sec.icon;
            return (
              <div key={sec.num} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <Icon size={22} />
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', marginBottom: '12px', lineHeight: 1.3 }}>
                  {sec.num}. {sec.title}
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                  {sec.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* BOTTOM QUESTIONS CONTACT BANNER */}
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '20px', padding: '24px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Shield size={22} />
            </div>
            <div>
              <h4 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 4px 0' }}>Questions?</h4>
              <p style={{ fontSize: '12.5px', color: '#475569', margin: 0, fontWeight: 500 }}>
                If you have any questions about this Privacy Policy or how we handle your data, please contact us.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <a href="mailto:Info@infraedgeredlight.co.uk" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
              <Mail size={16} color="#dc2626" />
              <span>Info@infraedgeredlight.co.uk</span>
            </a>
            <a href="tel:01214556529" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
              <Phone size={16} color="#dc2626" />
              <span>0121 455 6529</span>
            </a>
            <a href="https://activeacademy.co.uk/red-light-infrared-therapy" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
              <Globe size={16} color="#dc2626" />
              <span>www.activeacademy.co.uk</span>
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PrivacyPolicy;
